import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { AlbumFile } from '@/models/albumFile.ts'

interface AlbumFilesEntry {
  albumId: string
  files: AlbumFile[]
}

export interface AllFilesContextValue {
  // Registriert Dateien eines Albums
  registerAlbumFiles: (albumId: string, files: AlbumFile[]) => void
  // Deregistriert Dateien eines Albums
  unregisterAlbumFiles: (albumId: string) => void
  // Gibt das Album für eine Datei-ID zurück
  getAlbumIdForFile: (fileId: string) => string | undefined
  // Alle registrierten Album-Dateien
  albumFilesMap: Record<string, AlbumFilesEntry>
}

export const AllFilesContext = createContext<AllFilesContextValue | null>(null)

export function AllFilesProvider({ children }: { children: React.ReactNode }) {
  const [albumFilesMap, setAlbumFilesMap] = useState<Record<string, AlbumFilesEntry>>({})

  const registerAlbumFiles = useCallback((albumId: string, files: AlbumFile[]) => {
    setAlbumFilesMap(prev => {
      const existing = prev[albumId]
      if (existing && existing.files.length === files.length && existing.files === files) {
        return prev
      }
      return { ...prev, [albumId]: { albumId, files } }
    })
  }, [])

  const unregisterAlbumFiles = useCallback((albumId: string) => {
    setAlbumFilesMap(prev => {
      const newMap = { ...prev }
      delete newMap[albumId]
      return newMap
    })
  }, [])

  const getAlbumIdForFile = useCallback((fileId: string): string | undefined => {
    for (const entry of Object.values(albumFilesMap)) {
      if (entry.files.some(f => f.id === fileId)) {
        return entry.albumId
      }
    }
    return undefined
  }, [albumFilesMap])

  const value = useMemo(() => ({
    registerAlbumFiles,
    unregisterAlbumFiles,
    getAlbumIdForFile,
    albumFilesMap,
  }), [registerAlbumFiles, unregisterAlbumFiles, getAlbumIdForFile, albumFilesMap])

  return (
    <AllFilesContext.Provider value={value}>
      {children}
    </AllFilesContext.Provider>
  )
}

export function useAllFilesContext() {
  return useContext(AllFilesContext)
}
